import { ComponentProps, forwardRef, ReactElement } from 'react';
import styled from '@emotion/styled';
import MagnifierIcon from '../../../assets/icons/flights/magnifier.svg';
import { Color } from '../../../styles/Color';
import { CancelIconLight, Css, Responsive } from '../../../ui';
import { Shadow } from '../../../styles/Shadow';

const InputWrapper = styled.div`
	position: relative;
	width: 100%;
	height: 100%;
`;

export const StyledInput = styled.input<{
	dropdownIsOpen: boolean;
	hasPrefixIcon: boolean;
}>`
	padding: ${Css.pxToRem(28)} ${Css.pxToRem(56)} ${Css.pxToRem(8)};
	background: ${Color.PrimaryWhite};
	font-size: ${Css.pxToRem(16)};
	width: 100%;
	height: 100%;
	border: none;
	outline: none;
	border-bottom: 2px solid transparent;
	box-shadow: ${Shadow.Desktop};

	font-size: ${Css.pxToRem(18)};

	${({ hasPrefixIcon }) =>
		!hasPrefixIcon && `padding-left: ${Css.pxToRem(20)};`}

	&::placeholder {
		opacity: 0;
	}

	border-radius: ${({ dropdownIsOpen }) =>
		dropdownIsOpen ? `32px 32px 0 0 ` : '32px'};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;

const IconWrapper = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	padding: ${Css.pxToRem(22.5)};
	pointer-events: none;
	display: flex;
	align-items: center;
	color: ${Color.PrimaryBlue1};
`;

const MagnifierIconWrapper = styled.div`
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	pointer-events: none;
	align-items: center;

	display: flex;
	align-items: center;

	input:not(:placeholder-shown) ~ & {
		display: none;
	}
`;

const MagnifierButton = styled.button`
	pointer-events: all;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	background: none;
	padding: ${Css.pxToRem(20)};

	color: ${Color.PrimaryBlue1};
`;

const ClearWrapper = styled.div`
	position: absolute;
	right: 0;
	top: 0;
	bottom: 0;
	padding: ${Css.pxToRem(16)};
	pointer-events: none;
	align-items: center;
	display: none;

	input:not(:placeholder-shown) ~ & {
		display: flex;
	}
`;

const ClearButton = styled.button`
	pointer-events: all;
	border: none;
	padding: 0;
	margin: 0;
	cursor: pointer;
	border-radius: 50%;
	padding: ${Css.pxToRem(4)};
	transition: background 0.15s ease-in-out;

	&:hover {
		background: ${Color.Neutral80};
	}
`;

const Label = styled.label<{ hasPrefixIcon: boolean }>`
	position: absolute;
	top: 0;
	right: ${Css.pxToRem(56)};
	left: ${Css.pxToRem(56)};
	height: 100%;
	font-size: ${Css.pxToRem(18)};
	font-weight: 400;
	display: flex;
	align-items: center;
	transition: all 0.15s ease-in-out;
	transform-origin: top left;
	pointer-events: none;
	overflow: hidden;
	color: ${Color.Neutral};

	input:focus ~ & {
		color: ${Color.PrimaryBlue};
	}

	input:focus ~ &,
	input:not(:placeholder-shown) ~ & {
		font-size: ${Css.pxToRem(12)};
		height: 60%;
		color: ${Color.PrimaryBlue1};
	}

	${({ hasPrefixIcon }) => !hasPrefixIcon && `left: ${Css.pxToRem(20)};`}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;

const LabelTruncate = styled.span`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
`;

interface IProps extends Omit<ComponentProps<'input'>, 'placeholder'> {
	label: string;
	labelId?: string;
	icon?: ReactElement;
	className?: string;
	onClear: () => void;
	onQuerySubmit: () => void;
	dropdownIsOpen: boolean;
}

export const SearchInput = forwardRef<HTMLInputElement, IProps>(
	(
		{
			label,
			labelId,
			onClear,
			onQuerySubmit,
			id,
			dropdownIsOpen,
			icon,
			className,
			...inputProps
		},
		ref,
	) => (
		<InputWrapper className={className}>
			<StyledInput
				ref={ref}
				type="text"
				id={id}
				aria-labelledby={labelId}
				placeholder={label}
				dropdownIsOpen={dropdownIsOpen}
				hasPrefixIcon={!!icon}
				{...inputProps}
				onKeyUp={(event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						onQuerySubmit();
					}
				}}
			/>
			<Label htmlFor={id} id={labelId} hasPrefixIcon={!!icon}>
				<LabelTruncate>{label}</LabelTruncate>
			</Label>
			{icon && <IconWrapper>{icon}</IconWrapper>}

			<ClearWrapper>
				<ClearButton
					type="button"
					tabIndex={-1}
					aria-label="Clear search"
					onClick={onClear}
				>
					<CancelIconLight aria-label="Cancel" />
				</ClearButton>
			</ClearWrapper>
			<MagnifierIconWrapper>
				<MagnifierButton
					type="button"
					tabIndex={-1}
					aria-label="Search Flight"
					onClick={onQuerySubmit}
				>
					<MagnifierIcon width={24} height={24} />
				</MagnifierButton>
			</MagnifierIconWrapper>
		</InputWrapper>
	),
);

SearchInput.displayName = 'SearchInput';
